/**
 * Empty State Component
 *
 * Cartographic illustrations for each empty state variant.
 * Each illustration echoes the brand metaphor: maps, wayfinding, expedition.
 */

import { Link } from 'react-router-dom';
import {
  PlusCircle,
  ArrowRight,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { UI_LABELS } from '../lib/terminology';
import { TopoPatternBg } from './ui/TopoPatternBg';

// ============================================================================
// Types
// ============================================================================

export type EmptyStateVariant =
  | 'ideas'
  | 'items'
  | 'cycles'
  | 'focus_lists'
  | 'team'
  | 'results'
  | 'search'
  | 'generic';

export interface EmptyStateAction {
  label: string;
  path?: string;
  onClick?: () => void;
  primary?: boolean;
}

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  actions?: EmptyStateAction[];
  icon?: LucideIcon;
  showHelp?: boolean;
  helpLink?: string;
  className?: string;
}

// ============================================================================
// Cartographic SVG Illustrations
// ============================================================================

function IllustrationIdeas() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Parchment map */}
      <rect x="8" y="8" width="104" height="68" rx="3" fill="#F2F7F3" stroke="#B5D1BA" strokeWidth="1.5" strokeDasharray="5 3"/>
      {/* Corner registration marks */}
      <path d="M8,16 L8,8 L16,8" stroke="#84B18C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M104,16 L104,8 L112,8" stroke="#84B18C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8,68 L8,76 L16,76" stroke="#84B18C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M104,68 L104,76 L112,76" stroke="#84B18C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Contour lines */}
      <path d="M8,30 Q35,22 60,29 Q85,36 112,30" stroke="#D9E8DC" strokeWidth="0.8"/>
      <path d="M8,50 Q38,42 63,49 Q88,56 112,50" stroke="#D9E8DC" strokeWidth="0.8"/>
      {/* Central lightbulb */}
      <circle cx="60" cy="42" r="14" fill="#FEF8DF" stroke="#E3B505" strokeWidth="1.5"/>
      <path d="M56,39 Q56,34 60,32 Q64,30 64,35 Q64,39 61,41.5 L61,45 L59,45 L59,41.5 Q56,39 56,39Z" fill="#E3B505"/>
      <line x1="59" y1="45" x2="61" y2="45" stroke="#E3B505" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="59" y1="47.5" x2="61" y2="47.5" stroke="#E3B505" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Idea rays */}
      <line x1="60" y1="25" x2="60" y2="22" stroke="#E3B505" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="71" y1="28.5" x2="73" y2="26.5" stroke="#E3B505" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="49" y1="28.5" x2="47" y2="26.5" stroke="#E3B505" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="75" y1="38" x2="78" y2="37" stroke="#E3B505" strokeWidth="1" strokeLinecap="round"/>
      <line x1="45" y1="38" x2="42" y2="37" stroke="#E3B505" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}

function IllustrationItems() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Map background */}
      <rect x="6" y="6" width="108" height="72" rx="3" fill="#F2F7F3" stroke="#D9E8DC" strokeWidth="1"/>
      {/* Topographic rings — concentric ovals suggesting a peak */}
      <ellipse cx="60" cy="42" rx="46" ry="30" stroke="#D9E8DC" strokeWidth="0.7"/>
      <ellipse cx="60" cy="42" rx="34" ry="21" stroke="#C8DEC9" strokeWidth="0.8"/>
      <ellipse cx="60" cy="42" rx="22" ry="13" stroke="#B5D1BA" strokeWidth="0.9"/>
      <ellipse cx="60" cy="42" rx="12" ry="7" stroke="#84B18C" strokeWidth="1"/>
      <ellipse cx="60" cy="42" rx="5" ry="3" fill="#5A7D5E" opacity="0.2" stroke="#5A7D5E" strokeWidth="0.8"/>
      {/* Pin 1 — outer ring, hollow */}
      <line x1="36" y1="50" x2="36" y2="40" stroke="#84B18C" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="36" cy="38" r="4" fill="white" stroke="#84B18C" strokeWidth="1.5"/>
      {/* Pin 2 — center, filled (highest priority) */}
      <line x1="60" y1="33" x2="60" y2="25" stroke="#5A7D5E" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="60" cy="23" r="4.5" fill="#5A7D5E"/>
      <circle cx="60" cy="23" r="2" fill="white"/>
      {/* Pin 3 — mid ring */}
      <line x1="84" y1="52" x2="84" y2="42" stroke="#B5D1BA" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="84" cy="40" r="4" fill="white" stroke="#B5D1BA" strokeWidth="1.5"/>
    </svg>
  );
}

function IllustrationCycles() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Background */}
      <rect x="0" y="0" width="120" height="84" rx="4" fill="#F2F7F3"/>
      {/* Outer compass circle */}
      <circle cx="60" cy="42" r="34" stroke="#D9E8DC" strokeWidth="1.5" fill="white"/>
      {/* Middle ring */}
      <circle cx="60" cy="42" r="24" stroke="#EBF4EC" strokeWidth="0.8"/>
      {/* Cardinal ticks */}
      <line x1="60" y1="9" x2="60" y2="16" stroke="#5A7D5E" strokeWidth="2" strokeLinecap="round"/>
      <line x1="60" y1="68" x2="60" y2="75" stroke="#B5D1BA" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="27" y1="42" x2="34" y2="42" stroke="#B5D1BA" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="86" y1="42" x2="93" y2="42" stroke="#B5D1BA" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Intercardinal ticks */}
      <line x1="36" y1="18" x2="40" y2="22" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="84" y1="18" x2="80" y2="22" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="36" y1="66" x2="40" y2="62" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round"/>
      <line x1="84" y1="66" x2="80" y2="62" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round"/>
      {/* North needle — sage green */}
      <polygon points="60,16 57.5,42 60,44.5 62.5,42" fill="#5A7D5E"/>
      {/* South needle — light */}
      <polygon points="60,68 57.5,42 60,44.5 62.5,42" fill="#D9E8DC"/>
      {/* Pivot */}
      <circle cx="60" cy="42" r="3" fill="white" stroke="#5A7D5E" strokeWidth="1.5"/>
      {/* North dot */}
      <circle cx="60" cy="8" r="2.5" fill="#5A7D5E"/>
    </svg>
  );
}

function IllustrationFocusLists() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Pool frame */}
      <rect x="18" y="10" width="84" height="64" rx="2" stroke="#D9E8DC" strokeWidth="1.5" strokeDasharray="4 2" fill="#F9FBF9"/>
      {/* Bracket corner marks */}
      <path d="M18,22 L18,10 L30,10" stroke="#5A7D5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M90,22 L90,10 L102,10" stroke="#5A7D5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M18,62 L18,74 L30,74" stroke="#5A7D5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M90,62 L90,74 L102,74" stroke="#5A7D5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Selected row 1 */}
      <rect x="27" y="20" width="66" height="9" rx="2" fill="#D9E8DC"/>
      <circle cx="34" cy="24.5" r="3.5" fill="#5A7D5E"/>
      <path d="M32.5,24.5 L33.5,26 L36,23" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="40" y="23" width="32" height="3" rx="1" fill="#84B18C"/>
      {/* Selected row 2 */}
      <rect x="27" y="33" width="66" height="9" rx="2" fill="#D9E8DC"/>
      <circle cx="34" cy="37.5" r="3.5" fill="#5A7D5E"/>
      <path d="M32.5,37.5 L33.5,39 L36,36" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="40" y="36" width="26" height="3" rx="1" fill="#84B18C"/>
      {/* Unselected row 3 */}
      <rect x="27" y="46" width="66" height="9" rx="2" fill="#F2F7F3" stroke="#D9E8DC" strokeWidth="0.5"/>
      <circle cx="34" cy="50.5" r="3.5" stroke="#D9E8DC" strokeWidth="1.5" fill="white"/>
      <rect x="40" y="49" width="20" height="3" rx="1" fill="#E5EDEA"/>
      {/* Unselected row 4 */}
      <rect x="27" y="59" width="66" height="9" rx="2" fill="#F2F7F3" stroke="#D9E8DC" strokeWidth="0.5"/>
      <circle cx="34" cy="63.5" r="3.5" stroke="#D9E8DC" strokeWidth="1.5" fill="white"/>
      <rect x="40" y="62" width="28" height="3" rx="1" fill="#E5EDEA"/>
    </svg>
  );
}

function IllustrationTeam() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Sky */}
      <rect x="0" y="0" width="120" height="84" rx="4" fill="#F2F7F3"/>
      {/* Stars */}
      <circle cx="15" cy="14" r="1.2" fill="#E3B505"/>
      <circle cx="98" cy="10" r="1" fill="#E3B505"/>
      <circle cx="108" cy="22" r="1.2" fill="#E3B505"/>
      <circle cx="30" cy="8" r="0.8" fill="#E3B505"/>
      {/* Mountain silhouette */}
      <path d="M0,78 L30,40 L50,58 L70,22 L90,50 L105,34 L120,54 L120,84 L0,84 Z" fill="#D9E8DC"/>
      <path d="M62,34 L70,22 L78,36 Z" fill="#C8DEC9"/>
      {/* Summit flag */}
      <line x1="70" y1="22" x2="70" y2="13" stroke="#465F49" strokeWidth="1.5" strokeLinecap="round"/>
      <polygon points="70,13 79,16 70,19" fill="#E3B505"/>
      {/* Trail — dotted path */}
      <path d="M12,80 Q22,72 28,62 Q35,50 40,46 Q50,40 55,52" stroke="#5A7D5E" strokeWidth="1.5" strokeDasharray="3 2.5" fill="none" strokeLinecap="round"/>
      {/* Figure 1 — at base */}
      <circle cx="13" cy="76" r="3" fill="#5A7D5E"/>
      <path d="M11,80 L13,83.5 L15,80" stroke="#5A7D5E" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Figure 2 — midway */}
      <circle cx="35" cy="57" r="2.5" fill="#5A7D5E"/>
      <path d="M33.5,60 L35,63 L36.5,60" stroke="#5A7D5E" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Figure 3 — near top */}
      <circle cx="53" cy="49" r="2" fill="#84B18C"/>
      <path d="M52,52 L53,55 L54,52" stroke="#84B18C" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}

function IllustrationResults() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Sky */}
      <rect x="0" y="0" width="120" height="84" rx="4" fill="#F2F7F3"/>
      {/* Stars */}
      <circle cx="18" cy="16" r="1.5" fill="#E3B505"/>
      <circle cx="100" cy="12" r="1" fill="#E3B505"/>
      <circle cx="38" cy="10" r="1" fill="#E3B505"/>
      <circle cx="108" cy="28" r="1.5" fill="#E3B505"/>
      <circle cx="82" cy="8" r="1" fill="#E3B505"/>
      {/* Mountain range — background */}
      <path d="M0,78 L20,52 L38,66 L58,78 L78,60 L95,46 L110,58 L120,50 L120,84 L0,84 Z" fill="#EBF4EC"/>
      {/* Main peak — highlighted */}
      <path d="M42,78 L70,28 L98,78 Z" fill="#D9E8DC" stroke="#C8DEC9" strokeWidth="1"/>
      <path d="M62,42 L70,28 L78,42 Z" fill="#B5D1BA"/>
      {/* Summit zone */}
      <path d="M64,36 L70,28 L76,36 Z" fill="#84B18C"/>
      {/* Summit flag */}
      <line x1="70" y1="28" x2="70" y2="18" stroke="#354738" strokeWidth="1.5" strokeLinecap="round"/>
      <polygon points="70,18 80,21.5 70,25" fill="#E3B505"/>
      {/* Ground */}
      <line x1="0" y1="78" x2="120" y2="78" stroke="#C8DEC9" strokeWidth="0.5"/>
    </svg>
  );
}

function IllustrationSearch() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Map background */}
      <rect x="4" y="4" width="112" height="76" rx="3" fill="#F9FBF9" stroke="#E5EDEA" strokeWidth="1"/>
      {/* Grid lines */}
      <line x1="28" y1="4" x2="28" y2="80" stroke="#EBF2EB" strokeWidth="0.5"/>
      <line x1="52" y1="4" x2="52" y2="80" stroke="#EBF2EB" strokeWidth="0.5"/>
      <line x1="76" y1="4" x2="76" y2="80" stroke="#EBF2EB" strokeWidth="0.5"/>
      <line x1="100" y1="4" x2="100" y2="80" stroke="#EBF2EB" strokeWidth="0.5"/>
      <line x1="4" y1="26" x2="116" y2="26" stroke="#EBF2EB" strokeWidth="0.5"/>
      <line x1="4" y1="50" x2="116" y2="50" stroke="#EBF2EB" strokeWidth="0.5"/>
      {/* Contour suggestion */}
      <path d="M4,44 Q30,34 60,44 Q90,54 116,44" stroke="#D9E8DC" strokeWidth="0.8"/>
      {/* Magnifying glass body */}
      <circle cx="52" cy="38" r="22" fill="white" stroke="#84B18C" strokeWidth="2.5" opacity="0.95"/>
      {/* Foggy interior */}
      <circle cx="52" cy="38" r="19" fill="#F2F7F3"/>
      {/* Light contour inside lens */}
      <path d="M40,38 Q46,33 52,36 Q58,33 64,38" stroke="#D9E8DC" strokeWidth="1" fill="none"/>
      {/* Question mark path (no text element) */}
      <path d="M49,35 Q49,31 52,30 Q55,29 56,32 Q57,35 54,37 L54,39" stroke="#C8DEC9" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <circle cx="54" cy="41.5" r="1" fill="#C8DEC9"/>
      {/* Handle */}
      <line x1="68" y1="54" x2="82" y2="70" stroke="#5A7D5E" strokeWidth="3.5" strokeLinecap="round"/>
    </svg>
  );
}

function IllustrationGeneric() {
  return (
    <svg viewBox="0 0 120 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Parchment */}
      <rect x="8" y="6" width="104" height="72" rx="3" fill="#F2F7F3" stroke="#D9E8DC" strokeWidth="1.5"/>
      {/* Top worn edge */}
      <rect x="8" y="6" width="104" height="5" rx="2" fill="#E5EDEA"/>
      {/* Bottom worn edge */}
      <rect x="8" y="73" width="104" height="5" rx="2" fill="#E5EDEA"/>
      {/* Compass rose watermark */}
      <circle cx="60" cy="42" r="22" stroke="#D9E8DC" strokeWidth="1"/>
      <circle cx="60" cy="42" r="13" stroke="#E5EDEA" strokeWidth="0.8"/>
      <line x1="60" y1="20" x2="60" y2="64" stroke="#D9E8DC" strokeWidth="0.8"/>
      <line x1="38" y1="42" x2="82" y2="42" stroke="#D9E8DC" strokeWidth="0.8"/>
      <line x1="44.4" y1="26.4" x2="75.6" y2="57.6" stroke="#E5EDEA" strokeWidth="0.5"/>
      <line x1="75.6" y1="26.4" x2="44.4" y2="57.6" stroke="#E5EDEA" strokeWidth="0.5"/>
      {/* North needle */}
      <polygon points="60,20 58,34 60,36 62,34" fill="#C8DEC9"/>
      {/* South needle */}
      <polygon points="60,64 58,50 60,48 62,50" fill="#E5EDEA"/>
      {/* Pivot */}
      <circle cx="60" cy="42" r="3" fill="white" stroke="#D9E8DC" strokeWidth="1"/>
    </svg>
  );
}

// ============================================================================
// Variant Configurations
// ============================================================================

interface VariantConfig {
  illustration: () => React.JSX.Element;
  title: string;
  description: string;
  actions: EmptyStateAction[];
  helpText?: string;
}

const VARIANT_CONFIGS: Record<EmptyStateVariant, VariantConfig> = {
  ideas: {
    illustration: IllustrationIdeas,
    title: 'Uncharted Territory',
    description: 'Ideas are the starting point for every great expedition. Submit your first idea to begin charting the terrain.',
    actions: [
      { label: 'Submit an idea', path: '/prioritization/intake/submit', primary: true },
    ],
    helpText: 'Ideas capture requests, suggestions, or opportunities before they\'re ready to prioritize.',
  },
  items: {
    illustration: IllustrationItems,
    title: 'No Items to Prioritize',
    description: 'Shape ideas into items by adding the attributes needed for comparison.',
    actions: [
      { label: 'Shape Items', path: '/prioritization/items/readiness', primary: true },
      { label: 'Submit an idea', path: '/prioritization/intake/submit' },
    ],
    helpText: 'Items are ideas with attributes like impact, effort, and strategic alignment.',
  },
  cycles: {
    illustration: IllustrationCycles,
    title: 'No Cycles Charted',
    description: 'Create a cycle to begin a structured prioritization expedition.',
    actions: [
      { label: 'Create a Cycle', path: '/prioritization/cycles', primary: true },
    ],
    helpText: 'Cycles are the expedition containers — they scope, structure, and govern your prioritization work.',
  },
  focus_lists: {
    illustration: IllustrationFocusLists,
    title: `No ${UI_LABELS.itemPools}`,
    description: `Lock a comparison set before ranking begins — like fixing the jury before the trial starts.`,
    actions: [
      { label: 'View Cycles', path: '/prioritization/cycles', primary: true },
    ],
    helpText: `${UI_LABELS.itemPools} contain items selected for comparison. Lock the pool first, then rank.`,
  },
  team: {
    illustration: IllustrationTeam,
    title: 'No Expedition Members',
    description: 'Invite team members to join the expedition and participate in prioritization.',
    actions: [
      { label: 'Invite Members', path: '/prioritization/admin/users', primary: true },
    ],
    helpText: 'Team members can participate in sessions and view shared priorities.',
  },
  results: {
    illustration: IllustrationResults,
    title: 'The Summit Awaits',
    description: 'Run a prioritization session to generate results and chart your ranked priorities.',
    actions: [
      { label: 'Choose a Tool', path: '/prioritization/tools', primary: true },
    ],
    helpText: 'Results show how items rank based on your chosen method and criteria.',
  },
  search: {
    illustration: IllustrationSearch,
    title: 'No Results Found',
    description: 'The territory looks empty for this search. Try adjusting your terms or filters.',
    actions: [],
    helpText: 'Check for typos or try broader search terms.',
  },
  generic: {
    illustration: IllustrationGeneric,
    title: 'Nothing Here Yet',
    description: 'This area is empty. Take an action to begin mapping this territory.',
    actions: [],
  },
};

// ============================================================================
// Component
// ============================================================================

export function EmptyState({
  variant = 'generic',
  title,
  description,
  actions,
  icon: CustomIcon,
  showHelp = true,
  helpLink,
  className = '',
}: EmptyStateProps) {
  const config = VARIANT_CONFIGS[variant];

  const displayTitle = title || config.title;
  const displayDescription = description || config.description;
  const displayActions = actions || config.actions;
  const Illustration = config.illustration;

  return (
    <div className={`relative flex flex-col items-center justify-center py-12 px-6 ${className}`}>
      <TopoPatternBg opacity={0.05} />
      {/* Illustration or custom icon */}
      {CustomIcon ? (
        <div className="w-16 h-16 bg-[var(--hover-tint)] flex items-center justify-center mb-6">
          <CustomIcon className="h-8 w-8 text-[var(--text-ter)]" />
        </div>
      ) : (
        <div className="w-36 h-24 mb-6 overflow-hidden shadow-sm" aria-hidden="true">
          <Illustration />
        </div>
      )}

      {/* Title & Description */}
      <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2 text-center">
        {displayTitle}
      </h3>
      <p className="text-[var(--text-sec)] text-center max-w-md mb-6">
        {displayDescription}
      </p>

      {/* Actions */}
      {displayActions.length > 0 && (
        <div className="flex flex-wrap gap-3 justify-center mb-6">
          {displayActions.map((action, index) =>
            action.path ? (
              <Link
                key={index}
                to={action.path}
                className={`inline-flex items-center gap-2 px-4 py-2  font-medium transition-colors ${
                  action.primary
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                {action.primary && <PlusCircle className="h-4 w-4" />}
                {action.label}
                {!action.primary && <ArrowRight className="h-4 w-4" />}
              </Link>
            ) : (
              <button
                key={index}
                onClick={action.onClick}
                className={`inline-flex items-center gap-2 px-4 py-2  font-medium transition-colors ${
                  action.primary
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                {action.primary && <PlusCircle className="h-4 w-4" />}
                {action.label}
              </button>
            )
          )}
        </div>
      )}

      {/* Help Text */}
      {showHelp && config.helpText && (
        <div className="flex items-start gap-2 text-sm text-[var(--text-ter)] max-w-md text-center">
          <HelpCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{config.helpText}</span>
        </div>
      )}

      {/* Help Link */}
      {helpLink && (
        <Link
          to={helpLink}
          className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Learn more →
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// Specialized Empty State Components
// ============================================================================

export function EmptyIdeasState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="ideas" {...props} />;
}

export function EmptyItemsState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="items" {...props} />;
}

export function EmptyCyclesState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="cycles" {...props} />;
}

export function EmptyFocusListsState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="focus_lists" {...props} />;
}

export function EmptyResultsState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="results" {...props} />;
}

export function EmptySearchState(props: Partial<EmptyStateProps>) {
  return <EmptyState variant="search" showHelp={false} {...props} />;
}

export default EmptyState;
